import {
  Box,
  Grid,
  TextField,
  useMediaQuery,
  useTheme,
  Autocomplete,
} from "@mui/material";
import { useEffect, useState } from "react";
import { fetchSuppliers } from "../../api/adminSupplierApi";

interface Supplier {
  id: number;
  name: string;
}

interface FormData {
  supplier_id: number | null;
  new_supplier_name: string;
  invoice_number: string;
  order_number: string;
  delivery_date: string;
  invoice_date: string;
  note: string;
}

interface Props {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}

// Type guard for Supplier
const isSupplier = (option: unknown): option is Supplier =>
  typeof option === "object" && option !== null && "id" in option && "name" in option;

const ReceiveFormHeader = ({ formData, setFormData }: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  useEffect(() => {
    fetchSuppliers().then(setSuppliers).catch(console.error);
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Box>
      <Grid container spacing={isMobile ? 1 : 2}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={suppliers}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.name
            }
            value={formData.new_supplier_name}
            onChange={(_, selected) => {
              if (isSupplier(selected)) {
                setFormData((prev) => ({
                  ...prev,
                  supplier_id: selected.id,
                  new_supplier_name: selected.name,
                }));
              } else {
                setFormData((prev) => ({
                  ...prev,
                  supplier_id: null,
                  new_supplier_name: selected ?? "",
                }));
              }
            }}
            onInputChange={(_, value) => {
              setFormData((prev) => ({
                ...prev,
                new_supplier_name: value,
                supplier_id: null, // clear if manually typed
              }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Supplier"
                required
                size={isMobile ? "small" : "medium"}
              />
            )}
            freeSolo
            selectOnFocus
            clearOnBlur={false}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            size={isMobile ? "small" : "medium"}
            name="order_number"
            label="Order Number"
            value={formData.order_number}
            onChange={handleInputChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size={isMobile ? "small" : "medium"}
            name="invoice_number"
            label="Invoice Number"
            value={formData.invoice_number}
            onChange={handleInputChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="date"
            size={isMobile ? "small" : "medium"}
            name="delivery_date"
            label="Delivery Date"
            InputLabelProps={{ shrink: true }}
            value={formData.delivery_date}
            onChange={handleInputChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="date"
            size={isMobile ? "small" : "medium"}
            name="invoice_date"
            label="Invoice Date"
            InputLabelProps={{ shrink: true }}
            value={formData.invoice_date}
            onChange={handleInputChange}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            size={isMobile ? "small" : "medium"}
            name="note"
            label="Notes"
            multiline
            minRows={2}
            value={formData.note}
            onChange={handleInputChange}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReceiveFormHeader;
